import { Button, Input, InputGroup } from '@aus-platform/design-system';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import type { PasswordVerificationProps } from 'src/settings/types';
import { useVerifyPassword } from 'shared/api';
import { SectionHeader } from 'shared/components';
import { useInputFields } from 'shared/hooks';

type PasswordInput = {
  password: string;
};

const passwordInputState: PasswordInput = {
  password: '',
};

export const PasswordVerification: React.FC<PasswordVerificationProps> = ({
  className,
  backLabel,
  description,
  isLoading: isExternalLoading = false,
  onBack,
  onSuccess,
}) => {
  // Variables.
  const customClassname = classNames('settings-content', className);

  // Hooks.
  const {
    values,
    names,
    errors,
    setErrors,
    onChange,
    onBlur,
    onFocus,
    inputHasError,
  } = useInputFields<PasswordInput>(passwordInputState);

  // Apis.
  const {
    mutate: verifyPassword,
    isPending: isVerifyingPassword,
    isError: isVerifyPasswordError,
    data: verifyPasswordData,
  } = useVerifyPassword();

  // UseEffects.
  useEffect(() => {
    if (isVerifyPasswordError) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        password: 'Invalid Password. Please try again.',
      }));
    }

    if (verifyPasswordData) {
      onSuccess(verifyPasswordData.data.accessToken);
    }
  }, [isVerifyPasswordError, verifyPasswordData]);

  // Handlers.
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    verifyPassword({ password: values.password });
  };

  return (
    <div className={customClassname}>
      <form
        id="password-verification-form"
        className="settings-content__body password-verification"
        onSubmit={handleSubmit}
      >
        <SectionHeader
          backLabel={backLabel}
          description={description}
          onBack={onBack}
        />

        <InputGroup>
          <Input.Label>Password</Input.Label>
          <Input.Password
            placeholder="Enter your password"
            value={values.password}
            name={names.password}
            error={errors.password}
            isInvalid={!!errors.password}
            autoComplete="current-password"
            {...{ onChange, onBlur, onFocus }}
          />
        </InputGroup>
      </form>

      <div className="settings-content__footer">
        <Button
          type="submit"
          form="password-verification-form"
          isLoading={isVerifyingPassword || isExternalLoading}
          disabled={inputHasError()}
        >
          Verify
        </Button>
      </div>
    </div>
  );
};
